import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { BracketsSelector } from "@/components/doc/right-sidebar/active-node-style/group/BracketsSelector";
import { shouldOmitParentheses } from "@/components/expression/utils/functions";
import { Button } from "@/components/sidebar/Button";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { setFunctionParenthesesVisibility } from "@/input/utils/openGroup";

import type { DropdownItem } from "@/components/sidebar/Dropdown";
import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import type { ParenthesesVisibility } from "@corca/data";

type Props = {
  node: FunctionNodeModel;
};

export const FunctionBracketsSelector = observer(function FunctionBracketsSelector({
  node,
}: Props) {
  const t = useTranslations("sidebar.activeNode.function");
  const { doc } = node;
  const omitParentheses = shouldOmitParentheses(node);

  const handleOnVisibilityChange = (parenthesesVisibility: ParenthesesVisibility) => {
    doc.transact(() => {
      setFunctionParenthesesVisibility(node, parenthesesVisibility);
    }, "FunctionBracketsSelector:handleOnVisibilityChange");
    doc.focus();
  };

  const handleBracketsSelect = () => {
    doc.transact(() => {
      node.parenthesesVisibility = "visible";
    }, "FunctionBracketsSelector:handleBracketsSelect");
  };

  const items = [
    {
      title: t("parenthesesVisibility.options.visible"),
      onClick: () => handleOnVisibilityChange("visible"),
      active: node.parenthesesVisibility === "visible",
    },
    {
      title: t("parenthesesVisibility.options.hidden"),
      onClick: () => handleOnVisibilityChange("hidden"),
      active: node.parenthesesVisibility === "hidden",
    },
    {
      title: t("parenthesesVisibility.options.auto"),
      title2:
        node.parenthesesVisibility === "auto"
          ? `· ${t(`parenthesesVisibility.visibility.${omitParentheses ? "hidden" : "visible"}`)}`
          : undefined,
      onClick: () => handleOnVisibilityChange("auto"),
      active: node.parenthesesVisibility === "auto",
    },
  ] satisfies DropdownItem[];

  return (
    <>
      <div className="flex justify-between items-center mb-1.5">
        <div className="text-xxs tracking-tight uppercase">{t("parenthesesVisibility.title")}</div>
        <Dropdown
          items={items}
          toggleButtonComponent={
            <Button
              as="div"
              inverted
              title={t(`parenthesesVisibility.options.${node.parenthesesVisibility}`)}
              className="min-h-6 px-1 text-gray-500 font-mono text-xxs tracking-tight uppercase ring-1 ring-gray-300 rounded-full"
            >
              {node.parenthesesVisibility === "auto" && (
                <span className="text-document-text">
                  {t("parenthesesVisibility.options.auto")}·
                </span>
              )}
              {t(`parenthesesVisibility.visibility.${omitParentheses ? "hidden" : "visible"}`)}
            </Button>
          }
        />
      </div>
      {node.parenthesesVisibility !== "hidden" && (
        <BracketsSelector
          node={node}
          displayLimit={5}
          onSelect={handleBracketsSelect}
          className="mb-[5px]"
        />
      )}
    </>
  );
});
